<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import { setupVendorWebPush } from '@/plugins/webPush'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

const PUBLIC_AUTH_ROUTES = new Set([
  'auth-login',
  'auth-register',
  'auth-forgot-password',
  'auth-reset-password',
  'auth-email-verified',
])

export default {
  data() {
    return {
      baseApi: process.env.VUE_APP_APIENDPOINT,
    }
  },
  components: {
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    ScrollToTop,
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler(routeName) {
        this.enforceAuthRoute(routeName)
      },
    },
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleSaveState)
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleSaveState)
    if (localStorage.getItem('accessToken')) {
      this.$store.commit('app/LOAD_STATE')
      this.getAllStoreData()
      setInterval(() => {
        this.getAllStoreData()
      }, 120000)
      setupVendorWebPush()
    }
  },
  methods: {
    enforceAuthRoute(routeName) {
      if (!routeName) return
      const token = localStorage.getItem('accessToken')
      const isPublic = PUBLIC_AUTH_ROUTES.has(routeName)

      if (!token && !isPublic) {
        if (this.$route.name !== 'auth-login') {
          this.$router.replace({ name: 'auth-login' }).catch(() => {})
        }
        return
      }

      if (token && isPublic && routeName !== 'auth-login') {
        this.$router.replace({ name: 'my-poda-dashboard' }).catch(() => {})
      }
    },
    handleSaveState() {
      this.$store.commit('app/SAVE_STATE')
    },
  },
  beforeCreate() {
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
}
</script>
